// 配役結果のキャラカード画像 (1080×1350 の PNG) を作る。保存やシェア用。

import type { MemberView } from "../shared/api";
import type { Role } from "../shared/content";
import { content, getLocale, t } from "./lang";

const W = 1080;
const H = 1350;
const TITLE_FONT = '"Kaisei Decol", "Hiragino Mincho ProN", serif';
const BODY_FONT = '"Zen Maru Gothic", "Hiragino Sans", sans-serif';
const GOLD = "#f2c14e";
const PAPER = "#fbefdc";

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** 1行に収まるよう文字単位で折り返す (日本語向け) */
/** 行の先頭に来てはいけない文字 (句読点や閉じかっこ) */
const NO_LINE_START = "、。，．,.!?！？」』）)】〉》…ー〜";

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let lineText = "";
  for (const ch of text) {
    // 句読点は前の行にぶら下げて、行の先頭に来ないようにする
    if (ctx.measureText(lineText + ch).width > maxWidth && lineText && !NO_LINE_START.includes(ch)) {
      lines.push(lineText);
      lineText = ch;
    } else {
      lineText += ch;
    }
  }
  if (lineText) lines.push(lineText);
  return lines;
}

/** 英文は単語単位で折り返す */
function wrapWords(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let lineText = "";
  for (const word of text.split(" ")) {
    const next = lineText ? `${lineText} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && lineText) {
      lines.push(lineText);
      lineText = word;
    } else {
      lineText = next;
    }
  }
  if (lineText) lines.push(lineText);
  return lines;
}

export async function renderCardImage(role: Role, member: MemberView): Promise<Blob> {
  const c = content();
  const ui = t();
  // 日本語は文字単位、英語などは単語単位で折り返す
  const wrapText = getLocale() === "ja" ? wrap : wrapWords;
  await Promise.all([
    document.fonts.load(`700 100px ${TITLE_FONT}`, role.name),
    document.fonts.load(`500 40px ${BODY_FONT}`, ui.yourRoleIs),
  ]).catch(() => undefined);

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  // 背景と金の枠
  const bg = ctx.createRadialGradient(W / 2, 380, 50, W / 2, 600, 900);
  bg.addColorStop(0, "#4a1626");
  bg.addColorStop(1, "#140910");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 6;
  ctx.strokeRect(40, 40, W - 80, H - 80);
  ctx.lineWidth = 2;
  ctx.strokeRect(56, 56, W - 112, H - 112);

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";

  ctx.fillStyle = GOLD;
  ctx.font = `700 30px ${BODY_FONT}`;
  ctx.letterSpacing = "12px";
  ctx.fillText("BARD CLUB", W / 2, 130);
  ctx.letterSpacing = "0px";

  ctx.fillStyle = PAPER;
  ctx.font = `500 40px ${BODY_FONT}`;
  ctx.fillText(ui.yourRoleIs, W / 2, 200);

  // 名前が長いときは、枠に収まるまで文字を小さくする
  let nameSize = role.name.length > 7 && getLocale() === "ja" ? 92 : 112;
  ctx.font = `700 ${nameSize}px ${TITLE_FONT}`;
  while (ctx.measureText(role.name).width > W - 180 && nameSize > 56) {
    nameSize -= 4;
    ctx.font = `700 ${nameSize}px ${TITLE_FONT}`;
  }
  ctx.fillText(role.name, W / 2, 320);

  ctx.fillStyle = "#d8c3a8";
  ctx.font = `500 34px ${BODY_FONT}`;
  ctx.fillText(c.playTitle(role.play), W / 2, 380);

  // キャラの絵
  const halo = ctx.createRadialGradient(W / 2, 625, 20, W / 2, 625, 230);
  halo.addColorStop(0, "rgba(255, 233, 160, 0.95)");
  halo.addColorStop(1, "rgba(242, 193, 78, 0)");
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(W / 2, 625, 230, 0, Math.PI * 2);
  ctx.fill();
  const figure = await loadImage(`/img/chars/${role.id}.webp`);
  ctx.drawImage(figure, W / 2 - 181, 410, 362, 431);

  // 創立メンバーのしるし。主役はキャラなので、右下の角に小さく押す
  if (member.founding) {
    const badge = await loadImage("/img/founding.svg");
    ctx.save();
    ctx.translate(W - 134, H - 134);
    ctx.rotate(-0.15);
    ctx.shadowColor = "rgba(0, 0, 0, 0.4)";
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 3;
    ctx.drawImage(badge, -52, -52, 104, 104);
    ctx.restore();
  }

  // 人前 × ひとり
  ctx.fillStyle = GOLD;
  ctx.font = `700 38px ${BODY_FONT}`;
  ctx.fillText(`${c.inPublic(c.types[role.front].label)} × ${c.alone(c.types[role.back].label)}`, W / 2, 890);

  // 名台詞。日本語などは訳を大きく原文を小さく、英語は原文を大きくいまの英語の言い直しを小さく
  const english = getLocale() === "en";
  ctx.fillStyle = PAPER;
  ctx.font = english ? `italic 700 38px Georgia, "Times New Roman", serif` : `700 40px ${TITLE_FONT}`;
  let y = 970;
  const main = english ? wrapWords(ctx, role.quote.original, W - 220) : wrapText(ctx, role.quote.translation, W - 220);
  for (const l of main.slice(0, 3)) {
    ctx.fillText(l, W / 2, y);
    y += 56;
  }
  ctx.fillStyle = "#d8c3a8";
  ctx.font = english ? `500 28px ${BODY_FONT}` : `italic 28px Georgia, "Times New Roman", serif`;
  y += 8;
  for (const l of wrapWords(ctx, english ? role.quote.translation : role.quote.original, W - 240).slice(0, 3)) {
    ctx.fillText(l, W / 2, y);
    y += 38;
  }

  // 会員番号
  ctx.fillStyle = GOLD;
  ctx.font = `700 30px ${BODY_FONT}`;
  const no = `No.${String(member.number).padStart(8, "0")}`;
  ctx.fillText(member.founding ? `${no}  ${ui.founding}` : no, W / 2, H - 100);

  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error(ui.cardFailed))), "image/png"),
  );
}

/**
 * できあがった画像を画面に大きく出す。
 * 自動ダウンロードはブラウザによって止められて「何も起きない」ように見えるので、
 * まず画像を見せてから、シェア・ダウンロード・長押し保存のどれでも選べるようにする。
 */
// Chrome / Edge (パソコン) の「名前を付けて保存」ダイアログ。型定義にまだ無いので自前で書く
interface SaveFilePickerWindow {
  showSaveFilePicker?: (options: {
    suggestedName: string;
    types: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<{ createWritable: () => Promise<{ write: (data: Blob) => Promise<void>; close: () => Promise<void> }> }>;
}

export function showCardPreview(
  blob: Blob,
  filename: string,
  shareText: string,
  note?: string,
  /** 保存したあとに開くページ (パソコンで Instagram などに投稿するとき) */
  open?: { label: string; url: string },
): void {
  const ui = t();
  const url = URL.createObjectURL(blob);
  const file = new File([blob], filename, { type: "image/png" });
  // スマホ (と一部の Mac) は共有シートから「画像を保存」「"ファイル"に保存」が選べる
  const canShare = !!navigator.canShare?.({ files: [file] });
  // パソコンの Chrome / Edge は保存先を選べる
  const picker = (window as SaveFilePickerWindow).showSaveFilePicker;
  const isTouch = matchMedia("(pointer: coarse)").matches;

  const buttons: string[] = [];
  if (canShare) {
    buttons.push(
      `<button type="button" class="btn ${isTouch || !picker ? "btn-primary" : "btn-secondary"}" data-share>${
        isTouch ? ui.saveToPhotos : ui.saveFromShareMenu
      }</button>`,
    );
  }
  if (picker) {
    buttons.push(
      `<button type="button" class="btn ${isTouch && canShare ? "btn-secondary" : "btn-primary"}" data-pick>${ui.pickAndSave}</button>`,
    );
  }
  buttons.push(
    `<a class="btn ${buttons.length ? "btn-secondary" : "btn-primary"}" href="${url}" download="${filename}">${ui.saveToDownloads}</a>`,
  );
  if (open) {
    buttons.push(`<a class="btn btn-secondary" href="${open.url}" target="_blank" rel="noopener noreferrer">${open.label}</a>`);
  }
  buttons.push(`<button type="button" class="btn btn-secondary" data-close>${ui.close}</button>`);

  const dialog = document.createElement("dialog");
  dialog.className = "card-preview";
  dialog.setAttribute("aria-label", ui.card);
  dialog.innerHTML = `
    <img src="${url}" alt="${ui.card}" />
    ${note ? `<p class="card-preview-note">${note}</p>` : ""}
    ${isTouch ? `<p class="card-preview-hint">${ui.longPressHint}</p>` : ""}
    <div class="card-preview-actions">${buttons.join("")}</div>
    <p class="card-preview-status" role="status"></p>`;
  document.body.append(dialog);

  const status = dialog.querySelector<HTMLElement>(".card-preview-status")!;
  const close = () => dialog.close();
  dialog.addEventListener("close", () => {
    dialog.remove();
    URL.revokeObjectURL(url);
  });
  // 画像の外 (背景) を押しても閉じる
  dialog.addEventListener("click", (e) => {
    if (e.target === dialog) close();
  });
  dialog.querySelector("[data-close]")!.addEventListener("click", close);

  dialog.querySelector("[data-share]")?.addEventListener("click", async () => {
    try {
      await navigator.share({ files: [file], text: shareText });
    } catch {
      // キャンセルされたときは何もしない
    }
  });

  dialog.querySelector("[data-pick]")?.addEventListener("click", async () => {
    try {
      const handle = await picker!({
        suggestedName: filename,
        types: [{ description: ui.pngImage, accept: { "image/png": [".png"] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      status.textContent = ui.saved;
    } catch (e) {
      // 保存先を選ばずに閉じたときは何もしない
      if ((e as DOMException).name !== "AbortError") status.textContent = ui.saveFailedTryDownload;
    }
  });

  dialog.showModal();
}
